import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Alert, Typography, Checkbox, Modal } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Background image — served from public/login.jpg
const BG_IMAGE = '/login.jpg';

const Login = ({ onLogin }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotModalOpen, setForgotModalOpen] = useState(false);
  const [form] = Form.useForm();

  // Load remembered credentials on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('pts_remember');
      if (saved) {
        const { username, remember } = JSON.parse(saved);
        if (remember && username) {
          form.setFieldsValue({ username, remember: true });
        }
      }
    } catch (e) {
      // ignore
    }
  }, [form]);

  // Preload background to avoid flash
  useEffect(() => {
    const img = new Image();
    img.src = BG_IMAGE;
  }, []);

  const onFinish = async (values) => {
    setLoading(true);
    setError('');

    // Handle remember me
    try {
      if (values.remember) {
        localStorage.setItem('pts_remember', JSON.stringify({
          username: values.username,
          remember: true,
        }));
      } else {
        localStorage.removeItem('pts_remember');
      }
    } catch (e) {
      // ignore storage errors
    }

    try {
      const response = await api.post('/api/login', {
        username: values.username,
        password: values.password,
      });
      if (response.data.success) {
        onLogin(response.data.username, response.data.role);
      } else {
        setError(response.data.message || 'Invalid username or password');
      }
    } catch (err) {
      if (err.code === 'ERR_NETWORK') {
        setError('Cannot connect to server. Please check if backend is running.');
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: "'Segoe UI', 'Calibri', 'Helvetica Neue', Arial, sans-serif",
      }}
    >
      {/* Background image layer */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${BG_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          zIndex: 0,
        }}
      />

      {/* Subtle gradient overlay for contrast */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(135deg, rgba(15,58,77,0.35) 0%, rgba(14,116,144,0.22) 50%, rgba(15,58,77,0.40) 100%)',
          zIndex: 1,
        }}
      />

      {/* Form container — no card, fields float directly on image */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 420,
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        {/* Brand name (text only, no logo) */}
        <div
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: '#ffffff',
            letterSpacing: '-0.2px',
            lineHeight: 1.25,
            textShadow: '0 2px 16px rgba(0,0,0,0.6), 0 1px 4px rgba(0,0,0,0.4)',
            marginBottom: 28,
          }}
        >
          Phoenician Technical Services
        </div>

        {/* Title */}
        <div
          style={{
            fontSize: 26,
            fontWeight: 700,
            color: '#ffffff',
            letterSpacing: '-0.5px',
            marginBottom: 6,
            textShadow: '0 3px 20px rgba(0,0,0,0.55), 0 1px 6px rgba(0,0,0,0.4)',
            width: '100%',
          }}
        >
          Welcome back
        </div>

        {/* Subtitle — Petty Cash Portal */}
        <div
          style={{
            fontSize: 13,
            color: 'rgba(255,255,255,0.95)',
            lineHeight: 1.5,
            marginBottom: 24,
            fontWeight: 500,
            textShadow: '0 2px 10px rgba(0,0,0,0.5), 0 1px 4px rgba(0,0,0,0.35)',
            width: '100%',
          }}
        >
          Sign in to continue to the Petty Cash Portal
        </div>

        {/* Error */}
        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{
              width: '100%',
              marginBottom: 18,
              borderRadius: 10,
              background: 'rgba(220,38,38,0.18)',
              border: '1px solid rgba(220,38,38,0.35)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
            }}
            closable
            onClose={() => setError('')}
          />
        )}

        {/* Form */}
        <Form
          form={form}
          name="login"
          onFinish={onFinish}
          layout="vertical"
          size="large"
          initialValues={{ remember: false }}
          style={{ width: '100%' }}
          requiredMark={false}
        >
          <Form.Item
            name="username"
            label={<span style={labelStyle}>Username or Email</span>}
            rules={[{ required: true, message: 'Please enter your username' }]}
            style={{ marginBottom: 16 }}
          >
            <Input
              prefix={<UserOutlined style={{ color: 'rgba(255,255,255,0.9)' }} />}
              placeholder="Enter username or email"
              className="pts-input"
              style={inputStyle}
            />
          </Form.Item>

          <Form.Item
            name="password"
            label={<span style={labelStyle}>Password</span>}
            rules={[{ required: true, message: 'Please enter your password' }]}
            style={{ marginBottom: 12 }}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: 'rgba(255,255,255,0.9)' }} />}
              placeholder="Enter password"
              className="pts-input"
              style={inputStyle}
            />
          </Form.Item>

          {/* Remember Me + Forgot Password */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 22,
              marginTop: 4,
            }}
          >
            <Form.Item name="remember" valuePropName="checked" style={{ margin: 0 }}>
              <Checkbox className="pts-checkbox">
                <span
                  style={{
                    color: '#ffffff',
                    fontSize: 13,
                    fontWeight: 500,
                    textShadow: '0 1px 6px rgba(0,0,0,0.45)',
                  }}
                >
                  Remember me
                </span>
              </Checkbox>
            </Form.Item>

            <span
              onClick={() => setForgotModalOpen(true)}
              style={{
                color: '#ffffff',
                fontSize: 13,
                cursor: 'pointer',
                fontWeight: 600,
                textShadow: '0 1px 6px rgba(0,0,0,0.45)',
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.8')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            >
              Forgot Password?
            </span>
          </div>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              className="pts-submit"
              style={{
                height: 50,
                borderRadius: 10,
                fontWeight: 600,
                fontSize: 14,
                background: 'linear-gradient(135deg, #0e7490 0%, #0a5f78 100%)',
                border: 'none',
                boxShadow: '0 10px 30px rgba(14,116,144,0.5), 0 4px 12px rgba(0,0,0,0.2)',
                letterSpacing: '0.3px',
              }}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </Form.Item>
        </Form>

        {/* Secure connection footer */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
            color: 'rgba(255,255,255,0.8)',
            fontWeight: 500,
            marginTop: 32,
            paddingTop: 22,
            borderTop: '1px solid rgba(255,255,255,0.2)',
            textShadow: '0 1px 6px rgba(0,0,0,0.45)',
            width: '100%',
          }}
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{ marginRight: 6 }}
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
          Secure connection · 256-bit encrypted
        </div>

        {/* Copyright */}
        <div
          style={{
            fontSize: 11,
            color: 'rgba(255,255,255,0.9)',
            marginTop: 18,
            fontWeight: 500,
            textShadow: '0 1px 6px rgba(0,0,0,0.5)',
            letterSpacing: '0.2px',
            alignSelf: 'center',
          }}
        >
          © {new Date().getFullYear()} Phoenician Technical Services LLC
        </div>
      </div>

      {/* Forgot Password Modal (unchanged - kept as a clean, light modal) */}
      <Modal
        title={null}
        open={forgotModalOpen}
        onCancel={() => setForgotModalOpen(false)}
        footer={null}
        centered
        width={420}
        styles={{
          content: { borderRadius: 16, padding: 0, overflow: 'hidden' },
          body: { padding: 0 },
        }}
      >
        <div style={{ padding: '32px 28px 28px' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 14,
                background: 'linear-gradient(135deg, #e0f2f1, #b2dfdb)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}
            >
              <LockOutlined style={{ fontSize: 26, color: '#0e7490' }} />
            </div>
            <Title level={4} style={{ margin: '0 0 6px', color: '#0f3a4d', fontWeight: 700 }}>
              Forgot Password?
            </Title>
            <Text style={{ color: '#5a6d80', fontSize: 13 }}>
              Please contact the administrator to reset your password
            </Text>
          </div>

          <div
            style={{
              background: '#f0f7fa',
              border: '1px solid #d6e8ee',
              borderRadius: 12,
              padding: '20px',
              marginBottom: 20,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  background: '#fff',
                  border: '1px solid #d6e8ee',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <MailOutlined style={{ fontSize: 20, color: '#0e7490' }} />
              </div>
              <div>
                <Text
                  strong
                  style={{ display: 'block', fontSize: 13, color: '#0f3a4d', marginBottom: 4 }}
                >
                  Email Administrator
                </Text>
                <a
                  href="mailto:avishkar@phoenician-uae.com?subject=Password Reset Request - Petty Cash Portal&body=Hi,%0A%0AI need to reset my password for the Petty Cash Portal.%0A%0AMy username: %0A%0AThank you."
                  style={{
                    color: '#0e7490',
                    fontSize: 14,
                    fontWeight: 600,
                    textDecoration: 'none',
                    display: 'block',
                    wordBreak: 'break-all',
                  }}
                  onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
                  onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
                >
                  avishkar@phoenician-uae.com
                </a>
                <Text style={{ fontSize: 11, color: '#7a96a8', display: 'block', marginTop: 6 }}>
                  Please include your username in the email. Your password will be reset within 24 hours.
                </Text>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <Button
              block
              size="large"
              onClick={() => setForgotModalOpen(false)}
              style={{ borderRadius: 10, height: 44, fontWeight: 600, flex: 1 }}
            >
              Close
            </Button>
            <Button
              type="primary"
              block
              size="large"
              icon={<MailOutlined />}
              href="mailto:avishkar@phoenician-uae.com?subject=Password Reset Request - Petty Cash Portal&body=Hi,%0A%0AI need to reset my password for the Petty Cash Portal.%0A%0AMy username: %0A%0AThank you."
              style={{
                borderRadius: 10,
                height: 44,
                fontWeight: 600,
                flex: 1.5,
                background: 'linear-gradient(135deg, #0e7490, #0a5f78)',
                border: 'none',
              }}
            >
              Send Email
            </Button>
          </div>
        </div>
      </Modal>

      {/* Global styles for Ant Design inputs to match the transparent look */}
      <style>{`
        .pts-input.ant-input,
        .pts-input.ant-input-affix-wrapper,
        .pts-input.ant-input-password {
          background: rgba(255,255,255,0.18) !important;
          border: 1px solid rgba(255,255,255,0.35) !important;
          backdrop-filter: blur(14px) !important;
          -webkit-backdrop-filter: blur(14px) !important;
          box-shadow: 0 4px 20px rgba(0,0,0,0.12) !important;
          border-radius: 10px !important;
          height: 48px !important;
          color: #ffffff !important;
        }
        .pts-input.ant-input-affix-wrapper:hover,
        .pts-input.ant-input-affix-wrapper-focused,
        .pts-input.ant-input:hover,
        .pts-input.ant-input:focus {
          background: rgba(255,255,255,0.28) !important;
          border-color: rgba(255,255,255,0.6) !important;
          box-shadow: 0 0 0 3px rgba(255,255,255,0.18), 0 4px 20px rgba(0,0,0,0.15) !important;
        }
        .pts-input .ant-input {
          background: transparent !important;
          color: #ffffff !important;
          font-size: 14px !important;
          font-weight: 500 !important;
        }
        .pts-input .ant-input::placeholder,
        .pts-input input::placeholder {
          color: rgba(255,255,255,0.65) !important;
        }
        .pts-input .ant-input-suffix .anticon,
        .pts-input .ant-input-password-icon {
          color: rgba(255,255,255,0.85) !important;
        }
        .pts-input .ant-input-suffix .anticon:hover,
        .pts-input .ant-input-password-icon:hover {
          color: #ffffff !important;
        }

        /* Checkbox styling for transparent look */
        .pts-checkbox .ant-checkbox-inner {
          background: rgba(255,255,255,0.15) !important;
          border: 1.5px solid rgba(255,255,255,0.6) !important;
          border-radius: 5px !important;
          width: 18px !important;
          height: 18px !important;
          backdrop-filter: blur(8px) !important;
          -webkit-backdrop-filter: blur(8px) !important;
        }
        .pts-checkbox .ant-checkbox-checked .ant-checkbox-inner {
          background: rgba(14,116,144,0.85) !important;
          border-color: rgba(255,255,255,0.9) !important;
        }
        .pts-checkbox .ant-checkbox-checked .ant-checkbox-inner::after {
          border-color: #ffffff !important;
        }

        /* Form label color override */
        .ant-form-item-label > label {
          color: rgba(255,255,255,0.95) !important;
          font-weight: 600 !important;
          font-size: 12px !important;
          letter-spacing: 0.3px !important;
          text-shadow: 0 2px 8px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.35) !important;
        }

        /* Submit button hover */
        .pts-submit:hover:not(:disabled) {
          transform: translateY(-1px);
          filter: brightness(1.08);
        }
        .pts-submit:active:not(:disabled) {
          transform: translateY(0);
        }

        /* Error alert text white */
        .ant-alert-error .ant-alert-message {
          color: #ffffff !important;
          font-weight: 500 !important;
        }
        .ant-alert-error .ant-alert-icon {
          color: #ffffff !important;
        }
        .ant-alert-error .ant-alert-close-icon {
          color: rgba(255,255,255,0.7) !important;
        }

        /* Autofill fix - keep text white */
        .pts-input input:-webkit-autofill,
        .pts-input input:-webkit-autofill:hover,
        .pts-input input:-webkit-autofill:focus {
          -webkit-text-fill-color: #ffffff !important;
          -webkit-box-shadow: 0 0 0px 1000px rgba(15,58,77,0.3) inset !important;
          transition: background-color 5000s ease-in-out 0s;
        }
      `}</style>
    </div>
  );
};

// Shared inline styles
const labelStyle = {
  color: 'rgba(255,255,255,0.95)',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.3px',
  textShadow: '0 2px 8px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.35)',
};

const inputStyle = {
  borderRadius: 10,
  height: 48,
  color: '#ffffff',
  fontSize: 14,
};

export default Login;